<template>
  <section class="rounded-sm bg-[#232b33] p-4 roundedn-sm">
    <header class="flex items-center justify-between">
      <div class="flex items-center gap-2 cursor-pointer" @click="isExpanded = !isExpanded">
        <i :class="isExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        <h3 class="text-sm font-semibold text-white/90">Exec Summary</h3>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <span class="rounded bg-black/40 px-2 py-1 text-xs text-white/75">
          Incidents: <b class="text-white/90">{{ summary.incidentCount }}</b>
        </span>
        <span class="rounded bg-red-500/15 px-2 py-1 text-xs text-red-200">
          Critical: <b>{{ summary.criticalCount }}</b>
        </span>
        <span class="rounded bg-amber-500/15 px-2 py-1 text-xs text-amber-200">
          Warning: <b>{{ summary.warningCount }}</b>
        </span>
        <span v-if="summary.infoCount" class="rounded bg-sky-500/15 px-2 py-1 text-xs text-sky-200">
          Info: <b>{{ summary.infoCount }}</b>
        </span>
        <span v-if="summary.durationSeconds" class="rounded bg-black/40 px-2 py-1 text-xs text-white/60">
          {{ durationText }}
        </span>
        
        <span v-if="!summary.status" class="text-xs text-white/55">No report generated yet</span>
      </div>
    </header>

    <div v-if="isExpanded && loading" class="space-y-3">
      <div class="flex flex-wrap gap-2">
        <div class="h-6 w-20 animate-pulse rounded bg-white/10"></div>
        <div class="h-6 w-24 animate-pulse rounded bg-white/10"></div>
        <div class="h-6 w-16 animate-pulse rounded bg-white/10"></div>
        <div class="h-6 w-16 animate-pulse rounded bg-white/10"></div>
      </div>
      <div class="h-3 w-3/4 animate-pulse rounded bg-white/10"></div>
      <div class="grid grid-cols-2 gap-2 md:grid-cols-4">
        <div class="h-12 animate-pulse rounded bg-white/10"></div>
        <div class="h-12 animate-pulse rounded bg-white/10"></div>
        <div class="h-12 animate-pulse rounded bg-white/10"></div>
        <div class="h-12 animate-pulse rounded bg-white/10"></div>
      </div>
    </div>

    <div v-else-if="isExpanded" class="mt-3">
      

      <ul v-if="summary.highlights && summary.highlights.length" class="mb-3 space-y-2">
        <li
          v-for="(highlight, index) in summary.highlights"
          :key="index"
          class="flex items-start gap-2 text-sm text-white/80"
        >
          <i class="el-icon-warning-outline mt-0.5 text-amber-300"></i>
          <span>{{ highlight }}</span>
        </li>
      </ul>

      <div v-if="hasAiEventSummary" class="mb-3 rounded-sm bg-black/40 p-3 text-sm text-white/80">
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center gap-2 text-xs uppercase text-white/55">
            <i class="el-icon-magic-stick text-amber-300"></i>
            <span>AI event summary</span>
          </div>
          <div v-if="aiEventSummary.model || aiEventSummary.generatedAt" class="flex flex-wrap gap-3 text-xs text-white/50">
            <span v-if="aiEventSummary.model"><b>Model:</b> {{ aiEventSummary.model }}</span>
            <span v-if="aiEventSummary.generatedAt"><b>Generated:</b> {{ formatDate(aiEventSummary.generatedAt) }}</span>
          </div>
        </div>

        <div class="space-y-3">
          <div v-if="aiEventSummary.summary">
            <div class="mb-0.5 text-xs font-semibold text-white/70">Summary</div>
            <div class="whitespace-pre-line">{{ aiEventSummary.summary }}</div>
          </div>

          <div v-if="aiEventSummary.keyFindings && aiEventSummary.keyFindings.length">
            <div class="mb-1 text-xs font-semibold text-white/70">Key findings</div>
            <ul class="space-y-1">
              <li
                v-for="(finding, index) in aiEventSummary.keyFindings"
                :key="`finding-${index}`"
                class="flex items-start gap-2"
              >
                <i class="el-icon-warning-outline mt-0.5 text-amber-300"></i>
                <span>{{ finding }}</span>
              </li>
            </ul>
          </div>

          <div v-if="aiEventSummary.recommendations && aiEventSummary.recommendations.length">
            <div class="mb-1 text-xs font-semibold text-white/70">Recommendations</div>
            <ul class="space-y-1">
              <li
                v-for="(recommendation, index) in aiEventSummary.recommendations"
                :key="`recommendation-${index}`"
                class="flex items-start gap-2"
              >
                <i class="el-icon-check mt-0.5 text-emerald-300"></i>
                <span>{{ recommendation }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div v-if="totalsList.length" class="grid grid-cols-2 gap-x-3 gap-y-1 sm:grid-cols-3">
        <div
          v-for="metric in totalsList"
          :key="metric.key"
          class="flex items-baseline justify-between gap-2 rounded-sm bg-black/40 px-2 py-1"
        >
          <span class="truncate text-xs text-white/55" :title="metric.label">{{ metric.label }}</span>
          <span class="shrink-0 text-sm font-semibold text-white/90">{{ formatMetricValue(metric.value) }}</span>
        </div>
      </div>

      <div v-if="resourceMetrics.length" class="mt-3">
        <button
          class="mb-1 flex items-center gap-1 text-xs uppercase text-white/55"
          @click="showResources = !showResources"
        >
          <i :class="showResources ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          Per-resource metrics
        </button>
        <div v-if="showResources" class="space-y-2">
          <div
            v-for="resource in resourceMetrics"
            :key="resource.resourceId || resource.resourceName"
            class="rounded-sm bg-black/40 p-2"
          >
            <div class="mb-1 flex items-center gap-1 text-xs font-semibold" :class="resourceMeta(resource.resourceType).colorClass">
              <i :class="resourceMeta(resource.resourceType).icon"></i>
              <span class="truncate">{{ resource.resourceName }}</span>
              <span class="opacity-80">· {{ resourceMeta(resource.resourceType).label }}</span>
              <span v-if="resource.sourceType" class="text-white/40">({{ resource.sourceType }})</span>
            </div>
            <div class="grid grid-cols-1 gap-1 md:grid-cols-2">
              <div
                v-for="metric in resource.metrics"
                :key="metric.key"
                class="text-xs text-white/80"
              >
                <span class="text-white/55">{{ metric.label }}:</span> {{ formatMetricValue(metric.value) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import moment from 'moment';
import { resourceTypeMeta } from '../utils/eventResourceType';

const props = defineProps({
  event: {
    type: Object,
    default: null,
  },
  summary: {
    type: Object,
    default: () => ({
      status: null,
      incidentCount: 0,
      criticalCount: 0,
      errorCount: 0,
      warningCount: 0,
      infoCount: 0,
      durationSeconds: 0,
      highlights: [],
      totals: {},
    }),
  },
  aiEventSummary: {
    type: Object,
    default: null,
  },
  metricTotals: {
    type: Array,
    default: () => [],
  },
  resourceMetrics: {
    type: Array,
    default: () => [],
  },
  thresholds: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const isExpanded = ref(true);
const showResources = ref(false);

function resourceMeta(type) {
  return resourceTypeMeta(type);
}
function humanize(value) {
  return (value || '')
    .toString()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
}
function formatMetricValue(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (!Number.isInteger(value)) return value.toFixed(3);
    return value.toLocaleString();
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return value == null ? '-' : String(value);
}
function formatDate(value) {
  const date = moment.utc(value);
  return date.isValid() ? date.format('DD/MM/YYYY HH:mm:ss') : value;
}

const durationText = computed(() => {
  const seconds = Number(props.summary.durationSeconds) || 0;
  if (seconds <= 0) return '';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}min` : `${hours}h`;
});
const totalsList = computed(() => {
  if (props.metricTotals && props.metricTotals.length) return props.metricTotals;
  const totals = (props.summary && props.summary.totals) || {};
  return Object.keys(totals).map((key) => ({
    key,
    label: humanize(key),
    value: totals[key],
  }));
});
const hasAiEventSummary = computed(() => {
  const value = props.aiEventSummary;
  if (!value || typeof value !== 'object') return false;
  return !!(
    value.summary
    || value.model
    || value.generatedAt
    || (Array.isArray(value.keyFindings) && value.keyFindings.length)
    || (Array.isArray(value.recommendations) && value.recommendations.length)
  );
});
</script>
